/**
 * フォーム自動バックアップ カスタムフック
 *
 * フォーム入力途中のデータをlocalStorageに自動保存し、
 * リロードや誤操作によるデータ消失を防ぐ機能を提供します。
 *
 * 重要: デフォルト値（初期値）から変更があった場合のみバックアップを保存します。
 *
 * @example
 * ```tsx
 * const [form] = Form.useForm();
 * const {
 *   hasBackup,
 *   backupData,
 *   restoreBackup,
 *   clearBackup,
 *   showRestoreConfirm,
 * } = useFormAutoBackup({
 *   formType: 'equipment',
 *   entityId: equipment?.id,
 *   form,
 *   enabled: isModalOpen,
 * });
 * ```
 */

import type { FormInstance } from 'antd';
import { App, Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_DEBOUNCE_MS,
  DEFAULT_EXPIRATION_DAYS,
  type FormBackupData,
  formatBackupTime,
  getBackupFromStorage,
  getBackupKey,
  isBackupExpired,
  removeBackupFromStorage,
  saveBackupToStorage,
} from '../utils/formBackup';

export interface UseFormAutoBackupOptions<T> {
  /** フォームの種類（バックアップキーの一部として使用） */
  formType: string;
  /** エンティティID（編集時のID、新規作成の場合は null/undefined） */
  entityId?: string | number | null;
  /** Ant Design の FormInstance */
  form: FormInstance<T>;
  /** フック有効フラグ（モーダル表示中のみ有効にする等） */
  enabled?: boolean;
  /** デバウンス時間（ミリ秒） */
  debounceMs?: number;
  /** 有効期限（日数） */
  expirationDays?: number;
  /** 除外するフィールド名（パスワード等） */
  excludeFields?: string[];
}

export interface UseFormAutoBackupReturn<T> {
  /** バックアップが存在するかどうか */
  hasBackup: boolean;
  /** バックアップデータ */
  backupData: FormBackupData<T> | null;
  /** バックアップを復元する */
  restoreBackup: () => void;
  /** バックアップを削除する */
  clearBackup: () => void;
  /** 復元中かどうか（フォーム監視を一時停止するため） */
  isRestoring: boolean;
  /** 復元確認ダイアログを表示する */
  showRestoreConfirm: () => void;
  /** バックアップキー（デバッグ用） */
  backupKey: string;
}

/**
 * デバウンス関数
 */
function debounce<Args extends unknown[]>(
  func: (...args: Args) => void,
  wait: number
): ((...args: Args) => void) & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, wait);
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}

/**
 * 指定されたフィールドを除外したデータを返す
 */
function excludeFieldsFromData<T extends Record<string, unknown>>(
  data: T,
  excludeFields: string[]
): T {
  if (excludeFields.length === 0) return data;

  const result = { ...data };
  for (const field of excludeFields) {
    delete result[field];
  }
  return result;
}

/**
 * 2つの値が等しいかどうかを深く比較する
 */
function deepEqual(a: unknown, b: unknown): boolean {
  // 同じ参照なら等しい
  if (a === b) return true;

  // 型が違えば等しくない
  if (typeof a !== typeof b) return false;

  // null/undefined の比較
  if (a === null || b === null) return a === b;
  if (a === undefined || b === undefined) return a === b;

  // 空文字列と null/undefined は同等とみなす（フォームでは同じ意味）
  if (
    (a === '' && (b === null || b === undefined)) ||
    (b === '' && (a === null || a === undefined))
  ) {
    return true;
  }

  // 数値の比較（NaN対策）
  if (typeof a === 'number' && typeof b === 'number') {
    if (Number.isNaN(a) && Number.isNaN(b)) return true;
    return a === b;
  }

  // dayjs オブジェクトの比較
  if (
    a && typeof a === 'object' && '$d' in a &&
    b && typeof b === 'object' && '$d' in b
  ) {
    const aDate = a as { valueOf: () => number };
    const bDate = b as { valueOf: () => number };
    return aDate.valueOf() === bDate.valueOf();
  }

  // 配列の比較
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  // オブジェクトの比較
  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);

    // キーの数が違えば等しくない
    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every(key => deepEqual(aObj[key], bObj[key]));
  }

  return false;
}

/**
 * 初期値から変更があったフィールドのみを抽出する
 */
function getChangedFields<T extends Record<string, unknown>>(
  currentValues: T,
  initialValues: T
): Partial<T> {
  const changed: Partial<T> = {};

  for (const key of Object.keys(currentValues) as Array<keyof T>) {
    const currentValue = currentValues[key];
    const initialValue = initialValues[key];

    if (!deepEqual(currentValue, initialValue)) {
      changed[key] = currentValue;
    }
  }

  return changed;
}

/**
 * 変更があるかどうかをチェック
 */
function hasChanges<T extends Record<string, unknown>>(
  currentValues: T,
  initialValues: T
): boolean {
  return Object.keys(getChangedFields(currentValues, initialValues)).length > 0;
}

export function useFormAutoBackup<T extends Record<string, unknown>>(
  options: UseFormAutoBackupOptions<T>
): UseFormAutoBackupReturn<T> {
  const {
    formType,
    entityId,
    form,
    enabled = true,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    expirationDays = DEFAULT_EXPIRATION_DAYS,
    excludeFields = [],
  } = options;

  const [hasBackup, setHasBackup] = useState(false);
  const [backupData, setBackupData] = useState<FormBackupData<T> | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const { modal } = App.useApp();

  // バックアップキーを計算
  const backupKey = useMemo(
    () => getBackupKey(formType, entityId),
    [formType, entityId]
  );

  // 初回チェック済みフラグ
  const initialCheckDone = useRef(false);
  // 復元確認ダイアログ表示済みフラグ
  const confirmShown = useRef(false);
  // 初期値の記録
  const initialValuesRef = useRef<T | null>(null);
  // 初期値が設定されたかどうかのフラグ
  const initialValuesSet = useRef(false);
  // ユーザーが実際にフォームを操作したかどうか
  const userInteracted = useRef(false);
  // 初期値設定の安定化タイマーID
  const stabilizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // バックアップを削除
  const clearBackup = useCallback(() => {
    removeBackupFromStorage(backupKey);
    setHasBackup(false);
    setBackupData(null);
    // confirmShown.currentはリセットしない（同一セッション中は再表示しない）
  }, [backupKey]);

  // バックアップを復元
  const restoreBackup = useCallback(() => {
    if (!backupData) return;

    setIsRestoring(true);
    try {
      form.setFieldsValue(backupData.data as Parameters<typeof form.setFieldsValue>[0]);
    } finally {
      // 次のイベントループで復元フラグを解除
      setTimeout(() => {
        setIsRestoring(false);
      }, 0);
    }
  }, [backupData, form]);

  // 復元確認ダイアログを表示
  const showRestoreConfirm = useCallback(() => {
    if (!hasBackup || !backupData || confirmShown.current) return;

    confirmShown.current = true;
    modal.confirm({
      title: '入力途中のデータがあります',
      content: `${formatBackupTime(backupData.savedAt)}に保存されたデータがあります。復元しますか？`,
      okText: 'はい、復元する',
      cancelText: 'いいえ、新規入力',
      onOk: () => {
        restoreBackup();
        clearBackup(); // 復元後は一時データを削除
      },
      onCancel: () => {
        clearBackup();
        // フォームを初期値にリセット
        form.resetFields();
      },
    });
  }, [hasBackup, backupData, restoreBackup, clearBackup, modal, form]);

  // デバウンスされた保存関数
  const debouncedSave = useMemo(
    () =>
      debounce((values: T, initialValues: T | null, isTouched: boolean) => {
        if (!enabled) return;

        // ユーザーがフォームに触れていない場合はバックアップしない
        if (!isTouched) {
          return;
        }

        const dataToSave = excludeFieldsFromData(values, excludeFields);
        const initialDataToCompare = initialValues
          ? excludeFieldsFromData(initialValues, excludeFields)
          : ({} as T);

        // 初期値から変更があるかチェック
        if (!hasChanges(dataToSave, initialDataToCompare)) {
          // 変更がない場合はバックアップを削除
          removeBackupFromStorage(backupKey);
          return;
        }

        const entityIdStr = entityId != null ? String(entityId) : 'new';
        saveBackupToStorage(backupKey, formType, entityIdStr, dataToSave);
      }, debounceMs),
    [backupKey, formType, entityId, enabled, debounceMs, excludeFields]
  );

  // フォーム値の監視（Ant Design Form.useWatch）
  const formValues = Form.useWatch([], form);

  // フォーム値の変更を監視してバックアップ
  useEffect(() => {
    if (!enabled || isRestoring || !initialCheckDone.current) return;
    if (formValues === undefined) return;

    // 初期値がまだ設定されていない場合
    if (!initialValuesSet.current) {
      // 既存のタイマーをクリア
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
      }

      // 初期値の設定を遅延させる（setFieldsValueが完了するのを待つ）
      // フォーム値が安定するまで少し待ってから初期値として記録
      stabilizeTimerRef.current = setTimeout(() => {
        initialValuesRef.current = { ...formValues } as T;
        initialValuesSet.current = true;
        userInteracted.current = false; // ユーザー操作フラグをリセット
        stabilizeTimerRef.current = null;
      }, 300); // 300ms待機してフォームの初期化が完了するのを待つ

      return; // 初期値設定中はバックアップしない
    }

    // 初期値が設定された後の変更 = ユーザーの操作
    userInteracted.current = true;

    // ユーザーが操作した場合のみバックアップ
    debouncedSave(formValues as T, initialValuesRef.current, userInteracted.current);
  }, [formValues, enabled, isRestoring, debouncedSave]);

  // 初期化時にバックアップをチェック
  useEffect(() => {
    if (!enabled) {
      initialCheckDone.current = false;
      confirmShown.current = false;
      initialValuesSet.current = false;
      initialValuesRef.current = null;
      userInteracted.current = false;
      // タイマーをクリア
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
        stabilizeTimerRef.current = null;
      }
      return;
    }

    const backup = getBackupFromStorage<T>(backupKey);

    if (backup && !isBackupExpired(backup.savedAt, expirationDays)) {
      setHasBackup(true);
      setBackupData(backup);
    } else {
      // 有効期限切れまたは存在しない場合はクリア
      if (backup) {
        removeBackupFromStorage(backupKey);
      }
      setHasBackup(false);
      setBackupData(null);
    }

    initialCheckDone.current = true;
  }, [backupKey, enabled, expirationDays]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
      // タイマーもクリーンアップ
      if (stabilizeTimerRef.current) {
        clearTimeout(stabilizeTimerRef.current);
        stabilizeTimerRef.current = null;
      }
    };
  }, [debouncedSave]);

  return {
    hasBackup,
    backupData,
    restoreBackup,
    clearBackup,
    isRestoring,
    showRestoreConfirm,
    backupKey,
  };
}

export default useFormAutoBackup;
